import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { getToken } from '../auth';
import {
  Button,
  Input,
  Spin,
  Typography,
  Space,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseCircleOutlined,
  ForwardOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ExpandOutlined,
  CompressOutlined,
  RotateRightOutlined,
  HeartOutlined,
  HeartFilled,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  DownloadOutlined,
  FolderAddOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

interface ImageData {
  id: number;
  bucket_name: string;
  region: string;
  s3_url: string;
  thumbnail_uri: string;
  original_width: number;
  original_height: number;
  thumbnail_width: number;
  thumbnail_height: number;
  file_size_formatted: string;
  size_category: string;
  caption_summary?: string;
  rotation?: number;
}

interface Collection {
  id: number;
  name: string;
  image_count: number;
}

interface ModerationViewerProps {
  sessionId: number;
  sessionName: string;
  datasetType: string;
  currentIndex: number;
  totalImages: number;
  elapsedSeconds: number;
  lastResumedAt: string | null;
  // Live sessions track a growing source (dataset/parent). When the queue is
  // exhausted they wait for new images instead of showing a completion screen.
  isLive?: boolean;
  onClose: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const getThumbnailUrl = (image: ImageData) => {
  if (image.size_category === 'extra_tiny' || image.size_category === 'tiny' || !image.thumbnail_uri) {
    return image.s3_url;
  }
  return image.thumbnail_uri;
};

// Prefetch model: metadata is pipelined ahead of the current image to a fixed
// frontier (READAHEAD_AHEAD) by firing as many PREFETCH_CHUNK-sized batches as it
// takes to fill any gaps — in parallel, not one-at-a-time-when-low. This keeps the
// whole carousel strip populated so moderating never "catches up" to the loaded
// edge and stalls waiting on the next batch.
const PREFETCH_CHUNK = 12;   // fetch images 12 at a time
// Warm decoded pixels (and retain metadata) this far around the current image.
const PREFETCH_AHEAD = 15;
const PREFETCH_BEHIND = 5;   // keep a few moderated images behind for quick review-back

// In "Original" mode the displayed image is the full-resolution original, not
// the thumbnail — so we warm originals too. The window must be deep enough that
// rapid clicking can't outrun it (refilled on every switch), but decoded
// full-res bitmaps are tens of MB, so anything passed is released immediately.
const ORIGINAL_AHEAD = 8;
const ORIGINAL_BEHIND = 2;

// Evict metadata + decoded pixels beyond this window to bound memory; evicted
// bitmaps are released eagerly (see cleanupCache). Wider than the read-ahead
// frontier so freshly loaded images are never immediately evicted.
const EVICT_AHEAD = 32;
// Keep only the last 10 already-moderated (behind) images in memory; their
// decoded bitmaps otherwise pile up and exhaust browser memory on long sessions.
const EVICT_BEHIND = 10;

// The carousel renders only a tight window that follows the current image, so
// moderated thumbnails unmount and free their decoded pixels. Kept within the
// prefetched range so thumbnails entering the strip are already warm (no pop-in).
const CAROUSEL_BEHIND = 8;
const CAROUSEL_AHEAD = PREFETCH_AHEAD;

// Keep metadata loaded this far ahead of the current image: the whole carousel
// strip plus a chunk of margin, so batches are always in flight before the strip
// needs them. Sits comfortably inside EVICT_AHEAD so nothing is loaded then dropped.
const READAHEAD_AHEAD = CAROUSEL_AHEAD + PREFETCH_CHUNK; // 27

const ModerationViewer: React.FC<ModerationViewerProps> = ({
  sessionId,
  sessionName,
  datasetType,
  currentIndex: initialIndex,
  totalImages,
  elapsedSeconds: initialElapsed,
  lastResumedAt,
  isLive = false,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentImage, setCurrentImage] = useState<ImageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  // Set when the current image's metadata fetch fails after retries — shows a
  // Retry affordance instead of a permanent spinner.
  const [loadError, setLoadError] = useState(false);
  // Bumped by the Retry button to re-trigger the current-image fetch effect.
  const [retryNonce, setRetryNonce] = useState(0);
  // Image id whose full-res original has finished loading in the <img> element.
  // In Original mode we show a spinner until this matches the current image, then
  // reveal the original. Driven by the element's own onLoad (keyed per image) so
  // it can never trail behind navigation or stick on a previous image/thumbnail.
  const [originalLoadedId, setOriginalLoadedId] = useState<number | null>(null);
  // Image id whose original failed to load — fall back to the thumbnail as a last
  // resort so the spinner is bounded.
  const [originalErrorId, setOriginalErrorId] = useState<number | null>(null);
  const [captionPanelVisible, setCaptionPanelVisible] = useState(false);
  const [captionData, setCaptionData] = useState<any>(null);
  const [captionLoading, setCaptionLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(initialElapsed);
  // Running approved/declined tallies shown in the top bar. Seeded from the
  // session's persisted counters on mount, then mirrored locally on each
  // decision so they update in real time without waiting on the round-trip.
  const [approvedCount, setApprovedCount] = useState(0);
  const [declinedCount, setDeclinedCount] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [isReviewing, setIsReviewing] = useState(false); // true when navigated back to already-decided image
  const [currentDecision, setCurrentDecision] = useState<string | null>(null);
  // Per-image reviewer comment (stored on images.user_comment; captioning
  // tasks append it to the LLM prompt as extra context).
  const [imageComment, setImageComment] = useState('');
  const [commentSavedFlash, setCommentSavedFlash] = useState(false);
  // The image the loaded comment belongs to — saves target this, not whatever
  // is current at blur time, so a save can't land on the wrong image.
  const commentTargetRef = useRef<{ id: number; bucket: string; loaded: string } | null>(null);
  const commentFlashTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Latest textarea value, readable inside submitDecision without re-binding it.
  const imageCommentRef = useRef('');
  const commentInputRef = useRef<any>(null);

  // "Add to dataset" (Collection) state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [collectionBusy, setCollectionBusy] = useState(false);
  // Briefly flash a collection chip green after the current image is added to it
  // (message toasts render under the full-screen viewer overlay, so we give
  // inline feedback instead).
  const [flashedCollectionId, setFlashedCollectionId] = useState<number | null>(null);
  const flashTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Collections the CURRENT image already belongs to (an image can be in several).
  // Drives the highlight, and toggling: number/click adds, repeat removes.
  const [memberCollectionIds, setMemberCollectionIds] = useState<Set<number>>(new Set());
  const memberCollectionIdsRef = useRef<Set<number>>(memberCollectionIds);
  useEffect(() => { memberCollectionIdsRef.current = memberCollectionIds; }, [memberCollectionIds]);
  // User-defined display order (drag-and-drop), persisted per browser. Holds
  // collection ids; collections absent from it fall to the end in API order.
  const DATASET_ORDER_KEY = 'moderation-dataset-order';
  const [datasetOrder, setDatasetOrder] = useState<number[]>(() => {
    try { return JSON.parse(localStorage.getItem(DATASET_ORDER_KEY) || '[]'); }
    catch { return []; }
  });
  const persistOrder = useCallback((ids: number[]) => {
    setDatasetOrder(ids);
    try { localStorage.setItem(DATASET_ORDER_KEY, JSON.stringify(ids)); } catch { /* ignore quota */ }
  }, []);
  // Drag-and-drop reorder bookkeeping.
  const dragIdRef = useRef<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  // Width of the "Add to dataset" right column, resizable by dragging its left
  // border. Persisted per browser.
  const DATASET_PANEL_WIDTH_KEY = 'moderation-dataset-panel-width';
  const DATASET_PANEL_MIN = 140;
  const DATASET_PANEL_MAX = 560;
  const [datasetPanelWidth, setDatasetPanelWidth] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem(DATASET_PANEL_WIDTH_KEY) || '', 10);
    return Number.isFinite(saved) && saved > 0
      ? Math.min(DATASET_PANEL_MAX, Math.max(DATASET_PANEL_MIN, saved))
      : 200;
  });

  // Per-image display rotation. Seeded from the image's saved `rotation`, with
  // local overrides as the user rotates (persisted to the backend). An override
  // wins over the stored value so navigating back shows the latest choice.
  const [rotationOverrides, setRotationOverrides] = useState<Record<number, number>>({});
  const rotationOverridesRef = useRef(rotationOverrides);
  useEffect(() => { rotationOverridesRef.current = rotationOverrides; }, [rotationOverrides]);
  // Available area for the image, measured so a 90/270 rotation still fits.
  const imageBoxRef = useRef<HTMLDivElement>(null);
  const [imageBoxSize, setImageBoxSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Collections in the user's saved order; any not yet in the order (e.g. newly
  // created) are appended in the API's order. Numbered 1..N in this order, and
  // the first 9 get the matching digit-key shortcut.
  const orderedCollections = useMemo(() => {
    const byId = new Map(collections.map(c => [c.id, c]));
    const result: Collection[] = [];
    for (const id of datasetOrder) {
      const c = byId.get(id);
      if (c) { result.push(c); byId.delete(id); }
    }
    for (const c of collections) {
      if (byId.has(c.id)) result.push(c);
    }
    return result;
  }, [collections, datasetOrder]);
  const orderedCollectionsRef = useRef<Collection[]>(orderedCollections);
  useEffect(() => { orderedCollectionsRef.current = orderedCollections; }, [orderedCollections]);
  const highestIndexRef = useRef(initialIndex); // tracks furthest index reached (the "frontier")
  const decisionsMapRef = useRef<Map<number, { imageId: number; decision: string }>>(new Map());

  // Image metadata cache + retained decoded pixels for the prefetch window.
  const imageCacheRef = useRef<Map<number, ImageData>>(new Map());
  const loadingBatchesRef = useRef<Set<number>>(new Set());
  // Holds <img> elements so the browser keeps the decoded bitmap around; without
  // a live reference these can be garbage-collected and re-decoded on display.
  const preloadedRef = useRef<Map<number, HTMLImageElement>>(new Map());
  // Separate retain map for carousel-strip thumbnails. In Original mode preloadedRef
  // holds full-res originals, so the strip's thumbnails would be cold; this keeps
  // them decoded across the strip window so they never pop in as the strip slides.
  const thumbRetainRef = useRef<Map<number, HTMLImageElement>>(new Map());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Fire-and-forget decision queue
  const pendingDecisionsRef = useRef<Array<{ image_id: number; decision: string }>>([]);
  const drainingRef = useRef(false);
  // Ref to track currentImage for rapid decisions
  const currentImageRef = useRef<ImageData | null>(null);
  const currentIndexRef = useRef(initialIndex);
  // Mirror of showOriginal for use inside prefetch callbacks (avoids stale closures).
  const showOriginalRef = useRef(false);
  // Whether the current image is actually visible (metadata loaded, and in Original
  // mode the full-res original has loaded). Mirrored into a ref so submitDecision
  // can gate approve/decline on it. Kept in sync by an effect (see `canDecide`).
  const canDecideRef = useRef(false);

  // Carousel state
  const [carouselIndices, setCarouselIndices] = useState<number[]>([]);
  const carouselRef = useRef<HTMLDivElement>(null);
  const updateCarousel = useCallback(() => {
    const indices = Array.from(imageCacheRef.current.keys()).sort((a, b) => a - b);
    setCarouselIndices(indices);
  }, []);

  // Only the cached indices inside a tight window around the current image are
  // actually rendered. As the user advances, moderated thumbnails fall out of
  // this window, unmount, and their decoded pixels are released by the browser —
  // so the strip stays light and smooth no matter how large the queue is.
  const carouselWindow = useMemo(
    () => carouselIndices.filter(
      idx => idx >= currentIndex - CAROUSEL_BEHIND && idx <= currentIndex + CAROUSEL_AHEAD
    ),
    [carouselIndices, currentIndex]
  );

  // Live timer
  useEffect(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const additionalSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedSeconds(initialElapsed + additionalSeconds);
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [initialElapsed]);

  // Warm (and retain) the decoded image for a queue index. Warms the original
  // in "Original" mode and the thumbnail otherwise — i.e. whatever the main
  // viewer will actually display — so switching is instant. If the warmed
  // variant no longer matches the current mode, the old bitmap is released and
  // re-warmed with the correct URL.
  const preloadPixels = useCallback((index: number, img: ImageData) => {
    const url = showOriginalRef.current ? img.s3_url : getThumbnailUrl(img);
    const existing = preloadedRef.current.get(index);
    if (existing) {
      if (existing.src === url) return; // already warmed for the current mode
      existing.src = ''; // release the stale-mode bitmap before re-warming
    }
    const imgEl = new window.Image();
    imgEl.decoding = 'async';
    imgEl.src = url;
    // decode() forces the fetch AND the full off-main-thread decode to finish
    // now, not at first paint. Without it a "warmed" multi-MB original still
    // pays its decode cost the moment it's displayed — the visible lag when
    // rapidly switching in Original mode. Failures (eviction cleared src,
    // unsupported) just fall back to decode-on-display.
    imgEl.decode().catch(() => {});
    preloadedRef.current.set(index, imgEl);
  }, []);

  // Warm (and retain) a carousel thumbnail so it's decoded before it slides into
  // the strip. Only needed in Original mode — in thumbnail mode preloadPixels
  // already warms the very same URL, so callers skip this to avoid double work.
  const warmThumb = useCallback((index: number, img: ImageData) => {
    const url = getThumbnailUrl(img);
    const existing = thumbRetainRef.current.get(index);
    if (existing && existing.src === url) return;
    const imgEl = new window.Image();
    imgEl.decoding = 'async';
    imgEl.src = url;
    imgEl.decode().catch(() => {});
    thumbRetainRef.current.set(index, imgEl);
  }, []);

  const loadBatch = useCallback(async (startIndex: number, count: number = PREFETCH_CHUNK) => {
    if (startIndex < 0 || startIndex >= totalImages) return;
    if (loadingBatchesRef.current.has(startIndex)) return;
    loadingBatchesRef.current.add(startIndex);

    try {
      const response = await axios.get(
        `${API_URL}/moderation-sessions/${sessionId}/images-batch?startIndex=${startIndex}&count=${count}`,
        { timeout: 20000 }
      );
      const images: (ImageData & { _queueIndex: number })[] = response.data;
      images.forEach((img) => {
        imageCacheRef.current.set(img._queueIndex, img);
      });

      // Preload pixels in viewing order, nearest-first, for indices in the warm
      // window (tighter for full-res originals than for thumbnails).
      const center = currentIndexRef.current;
      const warmAhead = showOriginalRef.current ? ORIGINAL_AHEAD : PREFETCH_AHEAD;
      const warmBehind = showOriginalRef.current ? ORIGINAL_BEHIND : PREFETCH_BEHIND;
      images
        .slice()
        .sort((a, b) => Math.abs(a._queueIndex - center) - Math.abs(b._queueIndex - center))
        .forEach((img) => {
          if (img._queueIndex >= center - warmBehind && img._queueIndex <= center + warmAhead) {
            preloadPixels(img._queueIndex, img);
          }
        });

      // If this batch contains the current image and we're still loading, display it.
      if (imageCacheRef.current.has(center) && !currentImageRef.current) {
        const cached = imageCacheRef.current.get(center)!;
        setCurrentImage(cached);
        currentImageRef.current = cached;
        setThumbnailFailed(false);
        setLoading(false);
      }
      updateCarousel();
    } catch (error) {
      console.error('Failed to load image batch:', error);
    } finally {
      // Always release the in-flight marker so a hung / aborted / failed batch can
      // be retried; otherwise ensureWindow's dedup skips this range forever. On
      // success the metadata cache (imageCacheRef) prevents a redundant re-fetch.
      loadingBatchesRef.current.delete(startIndex);
    }
  }, [sessionId, totalImages, preloadPixels, updateCarousel]);

  // Keep the read-ahead buffer topped up and pixels warmed around `center`.
  const ensureWindow = useCallback((center: number) => {
    // AHEAD: pipeline-fill metadata up to the read-ahead frontier so the whole
    // carousel strip is always populated. Walk forward from the current image and
    // fire a chunk at each gap — multiple chunks go out in parallel, and re-firing
    // a chunk already in flight is a no-op (loadingBatchesRef dedupes). This is
    // what stops moderation from catching up to the loaded edge and stalling.
    const frontier = Math.min(totalImages - 1, center + READAHEAD_AHEAD);
    for (let probe = Math.max(0, center); probe <= frontier; ) {
      if (imageCacheRef.current.has(probe)) { probe++; continue; }
      loadBatch(probe, PREFETCH_CHUNK);
      probe += PREFETCH_CHUNK;
    }

    // BEHIND: ensure a small backward buffer for review-back (cheap no-op during
    // normal forward moderation, where these were just loaded as the read-ahead).
    const behindLo = Math.max(0, center - PREFETCH_BEHIND);
    if (behindLo < center && !imageCacheRef.current.has(behindLo)) {
      loadBatch(behindLo, center - behindLo); // exactly the backward buffer, no overlap with the ahead chunk
    }

    // Warm decoded pixels for everything cached in the warm window, nearest-first.
    // The window is tighter in Original mode since full-res bitmaps are heavy.
    const warmAhead = showOriginalRef.current ? ORIGINAL_AHEAD : PREFETCH_AHEAD;
    const warmBehind = showOriginalRef.current ? ORIGINAL_BEHIND : PREFETCH_BEHIND;
    const warmLo = Math.max(0, center - warmBehind);
    const warmHi = Math.min(totalImages - 1, center + warmAhead);
    for (let d = 0; d <= warmAhead; d++) {
      [center + d, center - d].forEach((i) => {
        if (i < warmLo || i > warmHi) return;
        const cached = imageCacheRef.current.get(i);
        if (cached) preloadPixels(i, cached);
      });
    }

    // Warm carousel thumbnails across the strip window. In thumbnail mode the warm
    // pass above already decoded these exact URLs; only Original mode needs it
    // (there the warm pass targets full-res originals, leaving strip thumbs cold).
    if (showOriginalRef.current) {
      const stripLo = Math.max(0, center - CAROUSEL_BEHIND);
      const stripHi = Math.min(totalImages - 1, center + CAROUSEL_AHEAD);
      for (let i = stripLo; i <= stripHi; i++) {
        const cached = imageCacheRef.current.get(i);
        if (cached) warmThumb(i, cached);
      }
    }
  }, [totalImages, loadBatch, preloadPixels, warmThumb]);

  // Evict cache + retained pixels well outside the window to bound memory.
  const cleanupCache = useCallback((center: number) => {
    const keepFrom = Math.max(0, center - EVICT_BEHIND);
    const keepTo = center + EVICT_AHEAD;
    let deleted = false;
    Array.from(imageCacheRef.current.keys()).forEach(key => {
      if (key < keepFrom || key > keepTo) {
        imageCacheRef.current.delete(key);
        deleted = true;
      }
    });
    // Decoded bitmaps are released at the (tighter) warm window rather than the
    // metadata window — critical in Original mode, where full-res bitmaps are
    // large and must not accumulate beyond the few we keep ready.
    const warmAhead = showOriginalRef.current ? ORIGINAL_AHEAD : PREFETCH_AHEAD;
    const warmBehind = showOriginalRef.current ? ORIGINAL_BEHIND : PREFETCH_BEHIND;
    const warmFrom = Math.max(0, center - warmBehind);
    const warmTo = center + warmAhead;
    Array.from(preloadedRef.current.entries()).forEach(([key, imgEl]) => {
      if (key < warmFrom || key > warmTo) {
        // Actively release the decoded bitmap instead of waiting for GC: clearing
        // src lets the browser drop the decoded pixels right away, which is what
        // actually frees memory on long sessions.
        imgEl.src = '';
        preloadedRef.current.delete(key);
      }
    });
    // Release retained strip thumbnails outside the carousel window.
    const stripFrom = Math.max(0, center - CAROUSEL_BEHIND);
    const stripTo = center + CAROUSEL_AHEAD;
    Array.from(thumbRetainRef.current.entries()).forEach(([key, imgEl]) => {
      if (key < stripFrom || key > stripTo) {
        imgEl.src = '';
        thumbRetainRef.current.delete(key);
      }
    });
    // Forget batches whose entire range is now outside the window. This bounds
    // loadingBatchesRef (it otherwise grows for the whole session) and lets a
    // batch reload cleanly if the user scrolls back into it later.
    Array.from(loadingBatchesRef.current).forEach(start => {
      if (start + PREFETCH_CHUNK - 1 < keepFrom || start > keepTo) {
        loadingBatchesRef.current.delete(start);
      }
    });
    if (deleted) updateCarousel();
  }, [updateCarousel]);

  // When the Original/thumbnail mode changes, re-warm the window for the new
  // variant (so the displayed image and the upcoming ones are preloaded) and
  // release decoded bitmaps that fall outside the new (tighter) warm window.
  useEffect(() => {
    showOriginalRef.current = showOriginal;
    ensureWindow(currentIndexRef.current);
    cleanupCache(currentIndexRef.current);
  }, [showOriginal, ensureWindow, cleanupCache]);

  // Load current image and manage batches
  useEffect(() => {
    currentIndexRef.current = currentIndex;
    if (currentIndex >= totalImages) {
      setCompleted(true);
      return;
    }
    // The queue grew (live session got new images) — leave the end-state and resume.
    setCompleted(false);

    // Determine if we're reviewing a previously decided image
    const reviewing = currentIndex < highestIndexRef.current;
    setIsReviewing(reviewing);
    const prevDecision = decisionsMapRef.current.get(currentIndex);
    setCurrentDecision(reviewing && prevDecision ? prevDecision.decision : null);

    setLoadError(false);

    // Try to display from cache
    let controller: AbortController | null = null;
    let cancelled = false;
    const cached = imageCacheRef.current.get(currentIndex);
    if (cached) {
      setCurrentImage(cached);
      currentImageRef.current = cached;
      setThumbnailFailed(false);
      setLoading(false);
    } else {
      setLoading(true);
      // Cache miss: fetch the single image, but make it cancellable + timed +
      // retrying so a stalled/aborted request can't leave the viewer spinning
      // forever, and a stale response can't overwrite a newer image.
      controller = new AbortController();
      const signal = controller.signal;
      const index = currentIndex;
      (async () => {
        for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
          try {
            const response = await axios.get(
              `${API_URL}/moderation-sessions/${sessionId}/image/${index}`,
              { signal, timeout: 15000 }
            );
            if (cancelled) return;
            imageCacheRef.current.set(index, response.data);
            setCurrentImage(response.data);
            currentImageRef.current = response.data;
            setThumbnailFailed(false);
            setLoading(false);
            return;
          } catch (error) {
            if (cancelled || signal.aborted) return;
            if (attempt < 2) {
              await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
              continue;
            }
            console.error('Failed to load image:', error);
            if (!cancelled) {
              setLoadError(true);
              setLoading(false);
            }
          }
        }
      })();
    }

    // Keep the prefetch window full, then drop anything far outside it.
    ensureWindow(currentIndex);
    cleanupCache(currentIndex);

    // Abandon an in-flight current-image fetch when we navigate away/unmount so
    // its late response can't clobber the newer current image.
    return () => {
      cancelled = true;
      if (controller) controller.abort();
    };
  }, [currentIndex, totalImages, sessionId, ensureWindow, cleanupCache, retryNonce]);

  // Toasts must render above the full-screen viewer overlay (z-index 9999),
  // otherwise success/error feedback is hidden underneath it.
  useEffect(() => {
    // zIndex is a valid runtime option but missing from antd v6's typings.
    message.config({ zIndex: 10020, top: 60 } as any);
  }, []);

  // Favorites and collections on mount (the index effect above handles image prefetch).
  useEffect(() => {
    axios.get(`${API_URL}/moderation-sessions/${sessionId}/favorites`)
      .then(res => setFavorites(new Set(res.data)))
      .catch(() => {});
    // Seed the approved/declined tallies from the persisted counters so a
    // resumed session shows the totals so far, not just this sitting's clicks.
    axios.get(`${API_URL}/moderation-sessions/${sessionId}`)
      .then(res => {
        setApprovedCount(res.data.approved_count || 0);
        setDeclinedCount(res.data.declined_count || 0);
      })
      .catch(() => {});
    fetchCollections();
  }, []);

  // Load caption when panel is open
  useEffect(() => {
    if (captionPanelVisible && currentImage) {
      fetchCaption(currentImage.id);
    }
  }, [captionPanelVisible, currentImage?.id]);

  // Which collections the current image is already in (for highlight + toggle).
  useEffect(() => {
    const imageId = currentImage?.id;
    if (!imageId) { setMemberCollectionIds(new Set()); return; }
    let cancelled = false;
    axios.get(`${API_URL}/images/${imageId}/collections`)
      .then(res => { if (!cancelled) setMemberCollectionIds(new Set(res.data?.collection_ids || [])); })
      .catch(() => { if (!cancelled) setMemberCollectionIds(new Set()); });
    return () => { cancelled = true; };
  }, [currentImage?.id]);

  // Track the image area's size so a 90/270 rotation can be scaled to fit.
  useEffect(() => {
    const el = imageBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setImageBoxSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fetchCaption = async (imageId: number) => {
    setCaptionLoading(true);
    try {
      const response = await axios.get(`${API_URL}/images/${imageId}/caption-json`);
      setCaptionData(response.data.caption_json);
    } catch {
      setCaptionData(null);
    } finally {
      setCaptionLoading(false);
    }
  };

  // Save the reviewer comment for the image it was loaded for (not whatever is
  // current at call time). No-op when unchanged. Fire-and-forget with a small
  // "Saved" flash on success.
  const saveComment = useCallback((text: string) => {
    const target = commentTargetRef.current;
    if (!target) return;
    const trimmed = text.trim();
    if (trimmed === target.loaded.trim()) return; // unchanged
    target.loaded = text;
    axios.put(`${API_URL}/images/${target.id}/comment`, {
      comment: trimmed,
      bucket_name: target.bucket,
    })
      .then(() => {
        setCommentSavedFlash(true);
        if (commentFlashTimerRef.current) clearTimeout(commentFlashTimerRef.current);
        commentFlashTimerRef.current = setTimeout(() => setCommentSavedFlash(false), 1200);
      })
      .catch(err => {
        console.error('Failed to save comment:', err);
        message.error('Failed to save comment');
      });
  }, []);

  // Load the comment when the image changes. Debounced like the old tag loads:
  // rapid clicking shouldn't fire a request per photo; comments only matter
  // once the user dwells. The target is set immediately (so typing before the
  // load resolves still saves to the right image), and the response never
  // clobbers text the user already typed.
  const commentDirtyRef = useRef(false);
  useEffect(() => { imageCommentRef.current = imageComment; }, [imageComment]);
  useEffect(() => {
    setImageComment('');
    commentDirtyRef.current = false;
    commentTargetRef.current = currentImage
      ? { id: currentImage.id, bucket: currentImage.bucket_name, loaded: '' }
      : null;
    // Keep the comment box focused so typing always lands in it.
    commentInputRef.current?.focus?.();
    if (!currentImage) return;
    const img = currentImage;
    const t = setTimeout(() => {
      axios.get(`${API_URL}/images/${img.id}/comment?bucket=${encodeURIComponent(img.bucket_name)}`)
        .then(res => {
          if (currentImageRef.current?.id === img.id && !commentDirtyRef.current) {
            setImageComment(res.data.comment);
            if (commentTargetRef.current?.id === img.id) {
              commentTargetRef.current.loaded = res.data.comment;
            }
          }
        })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [currentImage?.id]);

  // Drain pending decisions sequentially
  const drainDecisions = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    while (pendingDecisionsRef.current.length > 0) {
      const item = pendingDecisionsRef.current.shift()!;
      try {
        await axios.post(`${API_URL}/moderation-sessions/${sessionId}/decide`, {
          image_id: item.image_id,
          decision: item.decision,
          is_redecision: (item as any).is_redecision || false,
        });
      } catch (error) {
        console.error('Failed to submit decision:', error);
      }
    }
    drainingRef.current = false;
  }, [sessionId]);

  // Wait until every queued decision has been POSTed. Decisions are submitted
  // optimistically (the UI advances immediately while drainDecisions flushes the
  // queue in the background), so any handler that tears the viewer down — pause,
  // complete, close — must await this first or the still-queued votes are lost.
  const flushDecisions = useCallback(async () => {
    drainDecisions(); // kick a drain if one isn't already running
    while (pendingDecisionsRef.current.length > 0 || drainingRef.current) {
      await new Promise(r => setTimeout(r, 50));
    }
  }, [drainDecisions]);

  // Hard-close safety net: on tab close / navigation away, the async pause flush
  // can't run, so deliver any still-queued votes in one shot via sendBeacon to
  // the idempotent batch endpoint. Without this, fast votes made right before a
  // hard close were lost.
  useEffect(() => {
    const flushOnHide = () => {
      const queued = pendingDecisionsRef.current;
      if (queued.length === 0) return;
      const payload = JSON.stringify({ decisions: queued.splice(0) });
      try {
        navigator.sendBeacon(
          `${API_URL}/moderation-sessions/${sessionId}/decide-batch`,
          new Blob([payload], { type: 'application/json' })
        );
      } catch {
        // best-effort on unload
      }
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushOnHide(); };
    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushOnHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sessionId]);

  const submitDecision = useCallback((decision: 'approved' | 'declined' | 'skipped') => {
    if (!currentImageRef.current || completed) return;
    // Don't let approve/decline land on an image that isn't actually visible yet
    // (e.g. the full-res original is still loading in Original mode). Skip is
    // always allowed so a slow/broken original can never trap the moderator.
    if (decision !== 'skipped' && !canDecideRef.current) return;

    const imgId = currentImageRef.current.id;

    // Persist any comment typed for this image before advancing (no-op when
    // unchanged; targets the image the comment was loaded for).
    saveComment(imageCommentRef.current);

    if (isReviewing) {
      const prev = decisionsMapRef.current.get(currentIndex);
      if (prev && prev.decision !== decision) {
        pendingDecisionsRef.current.push({ image_id: imgId, decision, is_redecision: true } as any);
        drainDecisions();
        decisionsMapRef.current.set(currentIndex, { imageId: imgId, decision });
        // Move the tally off the old decision and onto the new one, mirroring
        // the redecision counter adjustment the backend makes.
        if (prev.decision === 'approved') setApprovedCount(c => Math.max(0, c - 1));
        else if (prev.decision === 'declined') setDeclinedCount(c => Math.max(0, c - 1));
        if (decision === 'approved') setApprovedCount(c => c + 1);
        else if (decision === 'declined') setDeclinedCount(c => c + 1);
      }
      setCurrentIndex(prev => prev + 1);
      setCaptionData(null);
      return;
    }

    decisionsMapRef.current.set(currentIndex, { imageId: imgId, decision });
    pendingDecisionsRef.current.push({ image_id: imgId, decision });
    drainDecisions();
    if (decision === 'approved') setApprovedCount(c => c + 1);
    else if (decision === 'declined') setDeclinedCount(c => c + 1);

    const nextIndex = currentIndex + 1;
    highestIndexRef.current = Math.max(highestIndexRef.current, nextIndex);
    setCurrentIndex(nextIndex);
    setCaptionData(null);
  }, [completed, drainDecisions, isReviewing, currentIndex, saveComment]);

  const toggleFavorite = useCallback(() => {
    if (!currentImageRef.current) return;
    const imgId = currentImageRef.current.id;
    const newFav = !favorites.has(imgId);
    setFavorites(prev => {
      const next = new Set(prev);
      if (newFav) next.add(imgId);
      else next.delete(imgId);
      return next;
    });
    axios.post(`${API_URL}/moderation-sessions/${sessionId}/favorite`, {
      image_id: imgId,
      is_favorite: newFav,
    }).catch(err => console.error('Failed to toggle favorite:', err));
  }, [sessionId, favorites]);

  const handleDownload = useCallback((variant: 'original' | 'thumbnail') => {
    const img = currentImageRef.current;
    if (!img) return;
    // The backend proxies the public R2 object and forces a save via
    // Content-Disposition, which sidesteps the *.r2.dev CORS restriction.
    // Browser-native download can't send the auth header, so pass the token as a
    // query param (the /api guard accepts ?token= as a fallback).
    const token = encodeURIComponent(getToken() || '');
    const a = document.createElement('a');
    a.href = `${API_URL}/images/${img.id}/download?variant=${variant}&token=${token}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/collections`);
      setCollections(res.data);
    } catch {
      // non-fatal; popover just shows an empty list
    }
  }, []);

  // Toggle the current image's membership in a collection: add if not a member,
  // remove if it already is. One image can be in several collections. The file
  // is never copied — the backend stores a reference (image_id + bucket_name)
  // and reads it live. Optimistic; reconciles to the server's count on response.
  const toggleCollectionMembership = useCallback((collection: Collection) => {
    // Instant feedback — flash the chip the moment it's triggered.
    setFlashedCollectionId(collection.id);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashedCollectionId(null), 800);

    const img = currentImageRef.current || currentImage;
    if (!img || !img.id || !img.bucket_name) {
      message.warning('Image not ready yet — try again in a moment');
      return;
    }

    const id = collection.id;
    const isMember = memberCollectionIdsRef.current.has(id);
    const bump = (delta: number) => setCollections(prev => prev.map(c =>
      c.id === id ? { ...c, image_count: Math.max(0, c.image_count + delta) } : c
    ));
    const reconcile = (res: any) => {
      if (typeof res?.data?.count === 'number') {
        setCollections(prev => prev.map(c => c.id === id ? { ...c, image_count: res.data.count } : c));
      }
    };

    if (isMember) {
      // Optimistic remove.
      setMemberCollectionIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      bump(-1);
      axios.delete(`${API_URL}/collections/${id}/images/${img.id}`)
        .then(reconcile)
        .catch((err) => {
          console.error('Remove from collection failed:', err);
          setMemberCollectionIds(prev => new Set(prev).add(id));
          bump(1);
          message.error(`Failed to remove from "${collection.name}"`);
        });
    } else {
      // Optimistic add.
      setMemberCollectionIds(prev => new Set(prev).add(id));
      bump(1);
      axios.post(`${API_URL}/collections/${id}/images`, {
        image_id: img.id,
        bucket_name: img.bucket_name,
      })
        .then(reconcile)
        .catch((err) => {
          console.error('Add to collection failed:', err);
          setMemberCollectionIds(prev => { const n = new Set(prev); n.delete(id); return n; });
          bump(-1);
          message.error(`Failed to add to "${collection.name}"`);
        });
    }
  }, [currentImage]);

  const createCollectionAndAdd = useCallback(async () => {
    const name = newCollectionName.trim();
    if (!name) return;
    setCollectionBusy(true);
    try {
      const res = await axios.post(`${API_URL}/collections`, { name });
      const created: Collection = res.data;
      setCollections(prev => [{ ...created, image_count: 0 }, ...prev]);
      // Show the new collection at the top of the user's order.
      persistOrder([created.id, ...datasetOrder.filter(oid => oid !== created.id)]);
      setNewCollectionName('');
      toggleCollectionMembership({ ...created, image_count: 0 });
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to create collection');
    } finally {
      setCollectionBusy(false);
    }
  }, [newCollectionName, toggleCollectionMembership, persistOrder, datasetOrder]);

  // Rotate the current image by ±90° (clockwise for +90). Metadata only — saved
  // to the backend and applied as a CSS transform; the file isn't re-encoded.
  const rotateCurrent = useCallback((deltaDeg: number) => {
    const img = currentImageRef.current || currentImage;
    if (!img || !img.id) {
      message.warning('Image not ready yet — try again in a moment');
      return;
    }
    const prev = rotationOverridesRef.current[img.id] ?? img.rotation ?? 0;
    const next = (((prev + deltaDeg) % 360) + 360) % 360;
    setRotationOverrides(o => ({ ...o, [img.id]: next }));
    axios.put(`${API_URL}/images/${img.id}/rotation`, { rotation: next, bucket_name: img.bucket_name })
      .catch((err) => {
        console.error('Save rotation failed:', err);
        setRotationOverrides(o => ({ ...o, [img.id]: prev }));
        message.error('Failed to save rotation');
      });
  }, [currentImage]);

  // Resize the dataset panel by dragging its left border. The panel is anchored
  // to the right, so dragging left (clientX decreasing) widens it.
  const startPanelResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = datasetPanelWidth;
    let latest = startW;
    const onMove = (ev: MouseEvent) => {
      latest = Math.min(DATASET_PANEL_MAX, Math.max(DATASET_PANEL_MIN, startW + (startX - ev.clientX)));
      setDatasetPanelWidth(latest);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { localStorage.setItem(DATASET_PANEL_WIDTH_KEY, String(latest)); } catch { /* ignore quota */ }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [datasetPanelWidth]);

  // Reorder via native HTML5 drag-and-drop; persist the new order per browser.
  const handleDatasetDrop = useCallback((targetId: number) => {
    const sourceId = dragIdRef.current;
    dragIdRef.current = null;
    setDragOverId(null);
    if (sourceId == null || sourceId === targetId) return;
    const ids = orderedCollectionsRef.current.map(c => c.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, sourceId);
    persistOrder(ids);
  }, [persistOrder]);

  const goBack = useCallback(() => {
    if (currentIndex <= 0) return;
    setCurrentIndex(prev => prev - 1);
    setCaptionData(null);
  }, [currentIndex]);

  const goForward = useCallback(() => {
    if (isReviewing) {
      setCurrentIndex(prev => prev + 1);
      setCaptionData(null);
    }
  }, [isReviewing]);

  const handlePauseAndClose = useCallback(async () => {
    // Flush queued votes BEFORE pausing/closing, otherwise decisions still in
    // the queue are dropped when the viewer unmounts (data loss on pause+close).
    await flushDecisions();
    try {
      await axios.post(`${API_URL}/moderation-sessions/${sessionId}/pause`);
    } catch (error) {
      console.error('Failed to pause session:', error);
    }
    onClose();
  }, [sessionId, onClose, flushDecisions]);

  const handleCompleteSession = useCallback(async () => {
    // Make sure every vote is persisted before the session is marked complete.
    await flushDecisions();
    try {
      await axios.post(`${API_URL}/moderation-sessions/${sessionId}/complete`);
    } catch (error) {
      console.error('Failed to complete session:', error);
    }
    onClose();
  }, [sessionId, onClose, flushDecisions]);

  // Scroll carousel to keep current image visible
  useEffect(() => {
    if (carouselRef.current) {
      const el = carouselRef.current.querySelector(`[data-idx="${currentIndex}"]`) as HTMLElement;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [currentIndex, carouselWindow]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (completed) {
        if (e.key === 'Escape') onClose();
        return;
      }
      // Digit 1-9 → toggle the current image's membership in the dataset shown at
      // that position in the "Add to dataset" list.
      if (/^[1-9]$/.test(e.key)) {
        const target = orderedCollectionsRef.current[Number(e.key) - 1];
        if (target) {
          e.preventDefault();
          toggleCollectionMembership(target);
        }
        return;
      }
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          submitDecision('declined');
          break;
        case 'ArrowRight':
          e.preventDefault();
          submitDecision('approved');
          break;
        case 'ArrowDown':
          e.preventDefault();
          submitDecision('skipped');
          break;
        case 'ArrowUp':
          e.preventDefault();
          goBack();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFavorite();
          break;
        case 'r':
          e.preventDefault();
          rotateCurrent(90);
          break;
        case 'R':
          e.preventDefault();
          rotateCurrent(-90);
          break;
        case ' ':
          e.preventDefault();
          setCaptionPanelVisible(prev => !prev);
          break;
        case 'Escape':
          handlePauseAndClose();
          break;
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 3) { // mouse back button
        e.preventDefault();
        goBack();
      } else if (e.button === 4) { // mouse forward button
        e.preventDefault();
        goForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, [completed, submitDecision, handlePauseAndClose, onClose, goBack, goForward, toggleFavorite, toggleCollectionMembership, rotateCurrent]);

  const formatTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // The current image is decidable once it's actually on screen: metadata loaded,
  // not completed, and — in Original mode — the full-res original has loaded (or
  // errored and fallen back to the thumbnail, which is also visible).
  const canDecide = !!currentImage && !loading && !completed &&
    (!showOriginal || originalLoadedId === currentImage.id || originalErrorId === currentImage.id);
  useEffect(() => { canDecideRef.current = canDecide; }, [canDecide]);

  const JSON_PANEL_WIDTH = 400;

  // Lock body scroll when viewer is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const preloaded = preloadedRef.current;
    const thumbRetained = thumbRetainRef.current;
    const cache = imageCacheRef.current;
    return () => {
      document.body.style.overflow = '';
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (commentFlashTimerRef.current) clearTimeout(commentFlashTimerRef.current);
      // Release all decoded bitmaps and cached metadata when the viewer closes.
      preloaded.forEach(imgEl => { imgEl.src = ''; });
      preloaded.clear();
      thumbRetained.forEach(imgEl => { imgEl.src = ''; });
      thumbRetained.clear();
      cache.clear();
    };
  }, []);

  // Effective rotation for the current image (local override wins over stored).
  const currentRotation = currentImage
    ? (rotationOverrides[currentImage.id] ?? currentImage.rotation ?? 0)
    : 0;
  const isQuarterTurn = currentRotation === 90 || currentRotation === 270;
  // On a quarter turn the image's footprint swaps W/H, so bound it by the
  // measured box (swapped) to keep it inside the viewport.
  const rotatedFit = isQuarterTurn && imageBoxSize.w > 0 && imageBoxSize.h > 0;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#000',
    }}>
      {/* Top Bar */}
      <div style={{
        height: 44,
        backgroundColor: '#000',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 16px',
        borderBottom: '1px solid #222',
        flexShrink: 0,
      }}>
        <Space size={4}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={goBack}
            disabled={currentIndex <= 0}
            style={{ color: currentIndex <= 0 ? '#444' : '#fff', minWidth: 32 }}
            title="Go back (↑)"
          />
          <Text style={{ color: '#fff', fontWeight: 600 }}>{sessionName}</Text>
          <Text style={{ color: '#555' }}>|</Text>
          <Text style={{ color: '#aaa' }}>
            {currentIndex + (completed ? 0 : 1)} / {totalImages}
          </Text>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={goForward}
            disabled={!isReviewing}
            style={{ color: !isReviewing ? '#444' : '#fff', minWidth: 32 }}
            title="Go forward"
          />
          {isReviewing && currentDecision && (
            <Text style={{
              color: currentDecision === 'approved' ? '#52c41a' : currentDecision === 'declined' ? '#ff4d4f' : '#888',
              fontSize: 12,
              textTransform: 'uppercase',
              fontWeight: 600,
            }}>
              {currentDecision}
            </Text>
          )}
        </Space>

        <Space size={20}>
          <Text style={{ color: '#52c41a', fontWeight: 600, fontSize: 15 }} title="Approved">
            <CheckOutlined style={{ marginRight: 5 }} />{approvedCount}
          </Text>
          <Text style={{ color: '#ff4d4f', fontWeight: 600, fontSize: 15 }} title="Declined">
            <CloseCircleOutlined style={{ marginRight: 5 }} />{declinedCount}
          </Text>
          <Text style={{ color: '#aaa', fontFamily: 'Monaco, monospace', fontSize: 16 }}>
            {formatTime(elapsedSeconds)}
          </Text>
        </Space>

        <Space>
          <Button
            type="text"
            icon={currentImage && favorites.has(currentImage.id) ? <HeartFilled /> : <HeartOutlined />}
            onClick={toggleFavorite}
            style={{ color: currentImage && favorites.has(currentImage.id) ? '#ff4d88' : '#fff' }}
            title="Favorite (F)"
          >
            Fav
          </Button>
          <Button
            type="text"
            icon={<RotateRightOutlined />}
            onClick={() => rotateCurrent(90)}
            disabled={!currentImage}
            style={{ color: currentRotation ? '#1890ff' : '#fff' }}
            title="Rotate 90° clockwise (R) · Shift+R counter-clockwise"
          >
            Rotate{currentRotation ? ` ${currentRotation}°` : ''}
          </Button>
          <Button
            type="text"
            icon={showOriginal ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setShowOriginal(prev => !prev)}
            style={{ color: showOriginal ? '#1890ff' : '#fff' }}
          >
            Original
          </Button>
          <Button
            type="text"
            icon={captionPanelVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setCaptionPanelVisible(prev => !prev)}
            style={{ color: '#fff' }}
          >
            Caption
          </Button>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload('original')}
            disabled={!currentImage}
            style={{ color: '#fff' }}
            title="Download original"
          >
            Save Orig
          </Button>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload('thumbnail')}
            disabled={!currentImage}
            style={{ color: '#fff' }}
            title="Download thumbnail"
          >
            Save Thumb
          </Button>
          <Button
            type="text"
            icon={<PauseCircleOutlined />}
            onClick={handlePauseAndClose}
            style={{ color: '#fff' }}
          >
            Pause & Close
          </Button>
        </Space>
      </div>

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Comment Panel - left column. Always focused so typing lands here
            immediately; the comment is saved when a decision is made (or on
            blur / Cmd+Enter). Escape leaves the box without closing the viewer. */}
        <div style={{
          width: 220,
          backgroundColor: '#141414',
          borderRight: '1px solid #222',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1a1a',
            flexShrink: 0,
          }}>
            <Text strong style={{ color: '#aaa', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
              <CommentOutlined style={{ marginRight: 6 }} />Comment
              {commentSavedFlash && (
                <Text style={{ color: '#52c41a', fontSize: 11, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>
                  Saved ✓
                </Text>
              )}
            </Text>
          </div>
          <div style={{ padding: '6px 8px', flexShrink: 0 }}>
            <Input.TextArea
              ref={commentInputRef}
              placeholder="Notes for captioning LLM..."
              value={imageComment}
              disabled={!currentImage}
              autoSize={{ minRows: 4, maxRows: 12 }}
              onChange={e => { commentDirtyRef.current = true; setImageComment(e.target.value); }}
              onBlur={() => saveComment(imageComment)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  // Leave the box; the next Escape (global) pauses the viewer.
                  (e.target as HTMLTextAreaElement).blur();
                  e.stopPropagation();
                  return;
                }
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  saveComment((e.target as HTMLTextAreaElement).value);
                }
                // Typing must not trigger decision shortcuts (arrows etc.).
                e.stopPropagation();
              }}
              style={{
                backgroundColor: '#1a1a1a',
                borderColor: '#434343',
                color: '#fff',
                fontSize: 13,
              }}
            />
            <Text style={{ color: '#555', fontSize: 11, display: 'block', marginTop: 6 }}>
              Saved on Approve / Decline. Used as extra context by captioning LLMs.
            </Text>
          </div>
        </div>

        {/* Image Area with nav buttons */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Prev Button */}
          <div
            onClick={(e) => { e.stopPropagation(); goBack(); }}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentIndex > 0 ? 'pointer' : 'default',
              opacity: currentIndex > 0 ? 0.8 : 0.2,
              color: '#fff',
              fontSize: 16,
              flexShrink: 0,
              margin: '0 4px',
            }}
          >
            <LeftOutlined />
          </div>

          {/* Image */}
          <div
            ref={imageBoxRef}
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              cursor: canDecide ? 'pointer' : undefined,
            }}
            onClick={(e) => {
              if (canDecide && e.button === 0) {
                submitDecision('declined');
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              if (canDecide) {
                submitDecision('approved');
              }
            }}
          >
            {completed && isLive ? (
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <Spin size="large" />
                <div style={{ fontSize: 24, margin: '24px 0 12px' }}>Waiting for new images…</div>
                <div style={{ fontSize: 16, color: '#aaa', marginBottom: 24 }}>
                  Caught up on all {totalImages} images. {approvedCount} approved · listening for new matches.
                </div>
                <Space size="large">
                  <Button type="primary" size="large" onClick={handleCompleteSession}>
                    Finish &amp; close
                  </Button>
                  <Button size="large" onClick={onClose} style={{ color: '#fff', borderColor: '#555' }}>
                    Close
                  </Button>
                </Space>
              </div>
            ) : completed ? (
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <CheckOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
                <div style={{ fontSize: 24, marginBottom: 12 }}>Session Complete!</div>
                <div style={{ fontSize: 16, color: '#aaa', marginBottom: 24 }}>
                  All {totalImages} images have been reviewed.
                </div>
                <Space size="large">
                  <Button type="primary" size="large" onClick={handleCompleteSession}>
                    Complete Session
                  </Button>
                  <Button size="large" onClick={onClose} style={{ color: '#fff', borderColor: '#555' }}>
                    Close
                  </Button>
                </Space>
              </div>
            ) : loading ? (
              <Spin size="large" />
            ) : loadError ? (
              <div style={{ textAlign: 'center', color: '#fff' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ fontSize: 16, marginBottom: 16, color: '#aaa' }}>Failed to load image.</div>
                <Button onClick={() => { setLoadError(false); setRetryNonce((n) => n + 1); }}>Retry</Button>
              </div>
            ) : currentImage ? (
              showOriginal && originalErrorId !== currentImage.id ? (
                // Original mode: keep a spinner up until the full-resolution original
                // for THIS image has loaded, then reveal it. The <img> is keyed by id
                // so onLoad always fires for the current original (no stale/trailing
                // state), and it loads while hidden so no thumbnail is ever shown.
                <>
                  {originalLoadedId !== currentImage.id && <Spin size="large" />}
                  <img
                    key={currentImage.id}
                    src={currentImage.s3_url}
                    alt={`ID ${currentImage.id}`}
                    decoding="async"
                    onLoad={() => setOriginalLoadedId(currentImage.id)}
                    onError={() => setOriginalErrorId(currentImage.id)}
                    style={{
                      maxWidth: rotatedFit ? imageBoxSize.h : '100%',
                      maxHeight: rotatedFit ? imageBoxSize.w : 'calc(100vh - 190px)',
                      objectFit: 'contain',
                      // Hidden (but still loading) until ready, so the spinner shows
                      // alone; revealed the moment onLoad fires.
                      display: originalLoadedId === currentImage.id ? 'block' : 'none',
                      transform: currentRotation ? `rotate(${currentRotation}deg)` : undefined,
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </>
              ) : (
                // Thumbnail mode (or the original failed to load): show the thumbnail,
                // falling back to the original on a thumbnail error.
                <img
                  src={thumbnailFailed ? currentImage.s3_url : getThumbnailUrl(currentImage)}
                  alt={`ID ${currentImage.id}`}
                  decoding="async"
                  style={{
                    // On a quarter turn, bound by the measured box with W/H swapped
                    // so the rotated image still fits; otherwise the usual limits.
                    maxWidth: rotatedFit ? imageBoxSize.h : '100%',
                    maxHeight: rotatedFit ? imageBoxSize.w : 'calc(100vh - 190px)',
                    objectFit: 'contain',
                    display: 'block',
                    transform: currentRotation ? `rotate(${currentRotation}deg)` : undefined,
                    transition: 'transform 0.2s ease',
                  }}
                  onError={() => {
                    if (!thumbnailFailed) {
                      setThumbnailFailed(true);
                    }
                  }}
                />
              )
            ) : (
              <Text style={{ color: '#888' }}>No image</Text>
            )}
          </div>

          {/* Next Button */}
          <div
            onClick={(e) => { e.stopPropagation(); goForward(); }}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isReviewing ? 'pointer' : 'default',
              opacity: isReviewing ? 0.8 : 0.2,
              color: '#fff',
              fontSize: 16,
              flexShrink: 0,
              margin: '0 4px',
            }}
          >
            <RightOutlined />
          </div>
        </div>

        {/* Drag handle to resize the right column by its left border */}
        <div
          onMouseDown={startPanelResize}
          title="Drag to resize"
          style={{
            width: 5,
            flexShrink: 0,
            cursor: 'col-resize',
            backgroundColor: '#1d1d1d',
            borderLeft: '1px solid #2a2a2a',
            borderRight: '1px solid #2a2a2a',
          }}
        />

        {/* Collections Panel - right column: click a dataset to add the current image */}
        <div style={{
          width: datasetPanelWidth,
          backgroundColor: '#141414',
          borderLeft: '1px solid #222',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '8px 12px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1a1a',
            flexShrink: 0,
          }}>
            <Text strong style={{ color: '#aaa', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
              <FolderAddOutlined style={{ marginRight: 6 }} />Add to dataset
            </Text>
          </div>
          <div style={{ padding: '4px 8px', borderBottom: '1px solid #222', flexShrink: 0 }}>
            <Input
              size="small"
              placeholder="New collection..."
              prefix={<PlusOutlined style={{ color: '#555' }} />}
              value={newCollectionName}
              onChange={e => setNewCollectionName(e.target.value)}
              onPressEnter={createCollectionAndAdd}
              onKeyDown={e => e.stopPropagation()}
              disabled={collectionBusy}
              style={{
                backgroundColor: '#1a1a1a',
                borderColor: '#434343',
                color: '#fff',
              }}
            />
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 8px', minHeight: 0 }}>
            {orderedCollections.map((c, idx) => {
              const flashed = flashedCollectionId === c.id;
              const isMember = memberCollectionIds.has(c.id);
              const isDragOver = dragOverId === c.id;
              const position = idx + 1;
              const hasShortcut = position <= 9;
              const bg = flashed ? '#237804' : isMember ? '#162312' : '#262626';
              const border = flashed || isMember ? '#52c41a' : '#434343';
              return (
                <div
                  key={c.id}
                  draggable
                  onDragStart={(e) => { dragIdRef.current = c.id; e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={(e) => { e.preventDefault(); if (dragOverId !== c.id) setDragOverId(c.id); }}
                  onDragLeave={() => { if (dragOverId === c.id) setDragOverId(null); }}
                  onDrop={(e) => { e.preventDefault(); handleDatasetDrop(c.id); }}
                  onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
                  onClick={(e) => { e.stopPropagation(); toggleCollectionMembership(c); }}
                  title={`${isMember ? 'Remove from' : 'Add to'} "${c.name}"${hasShortcut ? ` — key ${position}` : ''} · drag to reorder`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 4,
                    padding: '4px 6px',
                    borderRadius: 4,
                    border: `1px solid ${border}`,
                    borderTop: isDragOver ? '2px solid #1677ff' : `1px solid ${border}`,
                    backgroundColor: bg,
                    color: '#fff',
                    fontSize: 13,
                    cursor: currentImage ? 'pointer' : 'default',
                    opacity: currentImage ? 1 : 0.5,
                    userSelect: 'none',
                    transition: 'background-color 0.2s, border-color 0.2s',
                  }}
                >
                  <span style={{
                    flexShrink: 0,
                    minWidth: 18,
                    height: 18,
                    lineHeight: '18px',
                    textAlign: 'center',
                    borderRadius: 3,
                    fontSize: 11,
                    fontFamily: 'Monaco, monospace',
                    backgroundColor: hasShortcut ? '#333' : 'transparent',
                    border: hasShortcut ? '1px solid #555' : '1px solid transparent',
                    color: hasShortcut ? '#ddd' : '#666',
                  }}>{position}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {isMember ? '✓ ' : ''}{c.name}
                  </span>
                  <span style={{ opacity: 0.55, fontSize: 11, flexShrink: 0 }}>{c.image_count}</span>
                </div>
              );
            })}
            {orderedCollections.length === 0 && (
              <Text style={{ color: '#666', fontSize: 12 }}>No collections yet — type a name above.</Text>
            )}
            {orderedCollections.length > 0 && (
              <Text style={{ color: '#555', fontSize: 10, display: 'block', marginTop: 6, lineHeight: 1.4 }}>
                Press 1-9 to add/remove · drag to reorder
              </Text>
            )}
          </div>
        </div>

        {/* Caption Panel */}
        {captionPanelVisible && (
          <div style={{
            width: JSON_PANEL_WIDTH,
            backgroundColor: '#1e1e1e',
            borderLeft: '1px solid #333',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}>
            {currentImage && (
              <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid #333',
                backgroundColor: '#252526',
              }}>
                <Space size={8}>
                  <div>
                    <Text strong style={{ color: '#fff' }}>ID: {currentImage.id}</Text>
                    <br />
                    <Text style={{ fontSize: 11, color: '#888' }}>
                      {currentImage.original_width}x{currentImage.original_height}
                    </Text>
                  </div>
                </Space>
              </div>
            )}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {captionLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Spin />
                </div>
              ) : captionData ? (
                <pre style={{
                  margin: 0,
                  padding: 12,
                  fontSize: 11,
                  fontFamily: 'Monaco, Menlo, monospace',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(captionData, null, 2)}
                </pre>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Text style={{ color: '#888' }}>No caption data</Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Carousel Strip */}
      {!completed && carouselWindow.length > 0 && (
        <div
          ref={carouselRef}
          style={{
            height: 72,
            backgroundColor: '#0a0a0a',
            borderTop: '1px solid #222',
            display: 'flex',
            alignItems: 'center',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexShrink: 0,
            padding: '4px 8px',
            gap: 4,
          }}
        >
          {carouselWindow.map(idx => {
            const img = imageCacheRef.current.get(idx);
            if (!img) return null;
            const isCurrent = idx === currentIndex;
            const decision = decisionsMapRef.current.get(idx);
            const borderColor = isCurrent ? '#1890ff'
              : decision?.decision === 'approved' ? '#52c41a'
              : decision?.decision === 'declined' ? '#ff4d4f'
              : '#333';
            // The strip is always thumbnails — never the full-res original, even
            // when "Original" is toggled — so it can't balloon memory. lazy +
            // async decoding means the browser only decodes the thumbnails that
            // are actually scrolled into view.
            const imgSrc = getThumbnailUrl(img);
            return (
              <img
                key={idx}
                data-idx={idx}
                src={imgSrc}
                alt={`#${idx}`}
                decoding="async"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                  setCaptionData(null);
                }}
                style={{
                  height: 60,
                  width: 60,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: `2px solid ${borderColor}`,
                  opacity: isCurrent ? 1 : 0.6,
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'border-color 0.15s, opacity 0.15s',
                }}
              />
            );
          })}
        </div>
      )}

      {/* Bottom Bar - Decision Buttons */}
      {!completed && (
        <div style={{
          height: 56,
          backgroundColor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 24,
          borderTop: '1px solid #222',
          flexShrink: 0,
        }}>
          <Button
            danger
            size="large"
            icon={<CloseCircleOutlined />}
            onClick={() => submitDecision('declined')}
            disabled={!canDecide}
            style={{
              width: 160,
              height: 44,
              fontSize: 16,
              backgroundColor: '#ff4d4f',
              borderColor: '#ff4d4f',
              color: '#fff',
              opacity: canDecide ? 1 : 0.5,
            }}
          >
            Decline (←)
          </Button>
          <Button
            size="large"
            icon={<ForwardOutlined />}
            onClick={() => submitDecision('skipped')}
            disabled={loading}
            style={{
              width: 140,
              height: 44,
              fontSize: 16,
              backgroundColor: '#555',
              borderColor: '#555',
              color: '#fff',
            }}
          >
            Skip (↓)
          </Button>
          <Button
            size="large"
            icon={<CheckOutlined />}
            onClick={() => submitDecision('approved')}
            disabled={!canDecide}
            style={{
              width: 160,
              height: 44,
              fontSize: 16,
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              color: '#fff',
              opacity: canDecide ? 1 : 0.5,
            }}
          >
            Approve (→)
          </Button>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ModerationViewer;
